import { type ErrorResponse } from '@/types';
import { isHeidiSupportReachable } from '@/utils/heidiReach';
import { shouldSuppressDesktopOfflineNetworkErrorToast } from '@/utils/http/desktopOfflineNetwork';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import {
  getEhrConnectionErrorMessage,
  isEhrConnectionError,
} from '@heidi/integrations/ehr/data';
import { type useTranslations } from '@heidi/runtime/i18n';
import { sanitizeError } from '@heidi/runtime/logger';
import { AxiosError } from 'axios';

export const MAX_ATTEMPTS_ERROR_DESCRIPTION = isHeidiSupportReachable
  ? 'You have reached the maximum number of attempts to connect to this EHR, please contact support'
  : 'You have reached the maximum number of attempts to connect to this EHR, please contact your IT team';

type IntegrationErrorDeps = {
  t: ReturnType<typeof useTranslations>['t'];
  setIntegrationMaxAttemptsReached: (value: boolean) => void;
};

type IntegrationErrorConfig = {
  domainErrors?: Record<string, { title: string; description: string }>;
  fallbackTitle?: string;
  axiosFallbackTitle?: string;
};

export function handleIntegrationMutationError(
  error: Error,
  deps: IntegrationErrorDeps,
  config?: IntegrationErrorConfig
): void {
  const { t, setIntegrationMaxAttemptsReached } = deps;
  const fallbackTitle = config?.fallbackTitle ?? 'Failed';
  const axiosFallbackTitle = config?.axiosFallbackTitle ?? 'Error';

  // The app's single toast-layer offline predicate, shared with `useQueryUserEHR`
  // and the workspace team-details port. It only matches a failure that carried
  // no HTTP response, so the EHR 502 and max-attempts branches are out of reach.
  // `domainErrors` is not: it is a bare `error.message` lookup, so an entry
  // keyed on a message a transport failure can also produce ('Network Error')
  // becomes silently suppressible offline. Only the toast is suppressed; the
  // mutation still settles in error and nothing retries or queues the request.
  if (shouldSuppressDesktopOfflineNetworkErrorToast(error)) {
    // Warn, not error: reaching here means the app has already decided this
    // failure is the expected consequence of a link it cannot use, so error
    // level would be a Datadog error-rate signal saying nothing is wrong. Logged
    // rather than dropped because the toast was this failure's only trace, and
    // named for the outcome, not a cause — on the cold-boot grace arm the app
    // cannot yet tell whether it is actually offline.
    logger.warn(
      'Integration mutation transport failure, toast suppressed',
      sanitizeError(error)
    );
    return;
  }

  if (isEhrConnectionError(error)) {
    toast({
      tone: 'destructive',
      title: t('ehr.ehrConnectionError'),
      description: getEhrConnectionErrorMessage(error),
    });
    return;
  }

  const domainError = config?.domainErrors?.[error.message];
  if (domainError) {
    toast({
      tone: 'destructive',
      title: domainError.title,
      description: domainError.description,
    });
    return;
  }

  if (!(error instanceof AxiosError)) {
    toast({
      tone: 'destructive',
      title: fallbackTitle,
      description: 'Please try again',
    });
    return;
  }

  const errorResponse = error.response?.data as ErrorResponse;

  if (errorResponse?.detail?.type === 'EHRUserHitsDirectIntegrationMaxAttemptsError') {
    setIntegrationMaxAttemptsReached(true);
    toast({
      duration: Infinity,
      tone: 'destructive',
      title: 'Failed to connect integration',
      description: MAX_ATTEMPTS_ERROR_DESCRIPTION,
    });
    return;
  }

  toast({
    duration: Infinity,
    tone: 'destructive',
    title: axiosFallbackTitle,
    description: errorResponse?.detail?.msg,
  });
}
